import { t, Plural, Trans } from '@lingui/macro';
import classnames from 'classnames';
import React, { FC, ReactNode, memo } from 'react';

import shouldHideRatings from 'common/helpers/shouldHideRatings';
import routes from 'utils/routeTranslator';

import './L4Benefits.scss';
import maxResponseIcon from './img/check.svg';
import avgResponseIcon from './img/clock.svg';
import creditCardIcon from './img/credit-card.svg';
import registerProviderIcon from './img/user.svg';
import providerIcon from './img/users.svg';

type Props = {
  providersCount: number;
  responseTime: string;
  noProviders: boolean;
  averageFeedbacksValue: number;
  feedbacksCount: number;
  l2Slug: string;
  price?: number;
};

type Benefit = {
  title: string;
  description: ReactNode;
  icon: string;
  width: string;
  height: string;
  noProviders?: boolean;
};

const L4Benefit: FC<Benefit> = (props) =>
  props.noProviders ? (
    <div className="l4Benefit l4Benefit_noProviders">
      <div className="l4Benefit__header">
        <div className="l4Benefit__icon">
          <img src={props.icon} alt="" className="l4Benefit__img" width={props.width} height={props.height} />
        </div>
        <p className="l4Benefit__title">{props.title}</p>
      </div>
      <p className="l4Benefit__description">{props.description}</p>
    </div>
  ) : (
    <div className="l4Benefit">
      <div className="l4Benefit__icon">
        <img src={props.icon} alt="" className="l4Benefit__img" width={props.width} height={props.height} />
      </div>
      <div className="l4Benefit__label">
        <p className="l4Benefit__title item">{props.title}</p>
        <p className="l4Benefit__description">{props.description}</p>
      </div>
    </div>
  );

const spRegisterDescription = (slug: string) => (
  <Trans>
    Dołącz do grona polecanych wykonawców.{' '}
    <a href={routes.get('front::provider.register.l2', { slug })}>Zarejestruj się na Fixly</a> i zacznij zarabiać.
    Zdobywanie nowych zleceń jeszcze nigdy nie było takie proste!
  </Trans>
);

const L4Benefits: FC<Props> = (props) => {
  const providerCountBenefitDescription = shouldHideRatings(props.feedbacksCount, props.averageFeedbacksValue) ? (
    <Trans>świadczy tę usługę na Fixly</Trans>
  ) : (
    <Trans>
      ocenianych średnio na <span className="l4Benefits__rating">{props.averageFeedbacksValue.toFixed(1)}</span>
      <br /> (<span className="count">{props.feedbacksCount}</span>{' '}
      <Plural value={props.feedbacksCount} one="opinia" few="opinie" other="opinii" />)
    </Trans>
  );

  /**
   * Pay with Fixly promotion
   * see https://naspersclassifieds.atlassian.net/browse/GS-8019
   */

  const pwfBenefitItem: Benefit = {
    title: t`Cena usługi`,
    description: (
      <Trans>
        {props.price
          ? `Średni koszt realizacji tej usługi to ${props.price} zł`
          : 'Poznaj koszt realizacji usługi od wykonawcy'}
        <br />
        <a href={routes.get('front::pwf.promo')} target="_blank" rel="noreferrer noopener">
          Więcej informacji
        </a>
      </Trans>
    ),
    icon: creditCardIcon,
    width: '30',
    height: '30',
  };

  const benefits: Benefit[] = props.noProviders
    ? [
        {
          title: t`24 godziny`, // max response time
          description: t`mają wykonawcy na nawiązanie z Tobą kontaktu`,
          icon: maxResponseIcon,
          width: '40',
          height: '29',
        },
        {
          title: t`Wykonujesz tę usługę?`,
          description: spRegisterDescription(props.l2Slug),
          icon: registerProviderIcon,
          width: '35',
          height: '35',
        },
      ]
    : [
        {
          title: t`${props.providersCount < 10 ? 10 : props.providersCount} wykonawców`,
          description: providerCountBenefitDescription,
          icon: providerIcon,
          width: '41',
          height: '34',
        },
        pwfBenefitItem,
        {
          title: t`24 godziny`, // max response time
          description: t`mają wykonawcy na nawiązanie z Tobą kontaktu`,
          icon: maxResponseIcon,
          width: '40',
          height: '29',
        },
        {
          title: props.responseTime,
          description: t`zazwyczaj należy zaczekać na pierwszą wiadomość`,
          icon: avgResponseIcon,
          width: '34',
          height: '34',
        },
      ];

  const benefitsCN = classnames('l4Benefits', {
    l4Benefits_noProviders: props.noProviders,
  });

  return (
    <div className={benefitsCN}>
      {benefits.map((benefit) => (
        <L4Benefit
          title={benefit.title}
          description={benefit.description}
          icon={benefit.icon}
          width={benefit.width}
          height={benefit.height}
          key={benefit.title}
          noProviders={props.noProviders}
        />
      ))}
    </div>
  );
};

export default memo(L4Benefits);
